/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Enterprise is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Enterprise is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Enterprise, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Enterprise:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Enterprise broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - product: the product object (includes a list of all variants)
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the variant was added
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the quantity change was completed
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Enterprise utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Enterprise only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Enterprise.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Enterprise is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Enterprise provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyImages function - uses the IntersectionObserver API to lazy load images when needed.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

document.addEventListener('DOMContentLoaded', () => {
  var zendeskWebWidgetBtns = document.querySelector(
    '[data-target="zendesk-webwidget"]'
  );

  if (zendeskWebWidgetBtns) {
    zendeskWebWidgetBtns.addEventListener("click", function (e) {
      e.preventDefault();
      zE("webWidget", "show");
      zE("webWidget", "open");
    });
  }

  // added click handler on sign in icon when the list is set in the customizer.
  const signInIcon = document.querySelector('.sign-in-dropdown');
  const signInDropdownElement = document.querySelector('.sign-in .dropdown');
  const signInWithDataTabElements = document.querySelectorAll('.sign-in [data-tab]');

  if (signInWithDataTabElements) {
    // changing the cookie to open the desired tab on the "my account" page
    signInWithDataTabElements.forEach(element => {
      element.addEventListener('click', () => {
        const tabName = element.getAttribute("data-tab");
        localStorage.setItem("activeTab", tabName);
      });
    });
  }

  if (signInIcon && signInDropdownElement) {
    signInIcon.addEventListener('click', (e) => {
      e.preventDefault();
      signInDropdownElement.classList.toggle('active');
    });
  }

  // auto-part-search APP
  const openers = document.querySelectorAll(".auto-part-search-opener");
  const appPopUpElement = document.querySelector(".auto-part-search-bar");
  const bodyElement = document.querySelector("body");
  const openAppModal = (e, customView = false) => {
    e.preventDefault();
    if (appPopUpElement) {
      if (customView) {
        appPopUpElement.classList.add("show", "custom-view");
      } else {
        appPopUpElement.classList.remove("custom-view");
        appPopUpElement.classList.add("show");
      }

      bodyElement.classList.add("fixed");
    }
  };

  if (openers.length !== 0) {
    // click handler on openers
    openers.forEach((opener) => {
      opener.addEventListener("click", (e) => {
        if (opener.classList.contains("auto-part-custom-view")) {
          openAppModal(e, true);
        } else {
          openAppModal(e);
        }
      });
    });

    // close popup
    if (appPopUpElement) {
      const gvMmyBar = appPopUpElement.querySelector(".gv-mmy-bar");

      appPopUpElement.addEventListener("click", function (event) {
        if (!gvMmyBar.contains(event.target) && event.target !== gvMmyBar) {
          appPopUpElement.classList.remove("show");
          bodyElement.classList.remove("fixed");
        }
      });
    }
  }

  // Watch for the element "#gv-save-vehicles".

  // The function redirects the click from the button you pressed to the application button.
  function clickRedirectionOnAddCurrentEngine(e) {
    e.preventDefault();

    gvAddVehicle();
    //const addEngineButton = document.querySelector(
    //"#gv-save-vehicles .popup > .toggle.add-vehicle"
    // );
    // if (addEngineButton) {
    // addEngineButton.click();
    //  }
  }

  //Save/Update the selected MMY
  function gvAddVehicle() {
    var make = document.querySelector("#gv-mmy .mmy-make .mk").value;
    var model = document.querySelector("#gv-mmy .mmy-model .md").value;
    var year = document.querySelector("#gv-mmy .mmy-year .yr").value;
    if (make != "" && (model != "") & (year != "")) {
      var vehicle = new Object();
      vehicle.make = make;
      vehicle.model = model;
      vehicle.year = year;
      gvCreateCookie(MMY.jsonVehicleCookie, vehicle);
    }
    vehicleObect = (readCookie(MMY.jsonVehicleCookie) || { mmy: "" }).mmy;
    var vehicleJSONString = JSON.stringify(vehicleObect);
    console.log(vehicleJSONString);

    if (!(make != "" && model != "" && year != "")) {
      window.alert(selectVehicleLabel);
      return;
    }

    var cookie = readCookie(MMY.cookie);

    var jsonVehicles;
    var alreadySaved = false;

    // check if vehicle is already saved
    try {
      jsonVehicles = JSON.parse(vehicles);

      for (var i = 0; i < jsonVehicles.length; i++) {
        if (
          jsonVehicles[i].make == vehicleObect.make &&
          jsonVehicles[i].model == vehicleObect.model &&
          jsonVehicles[i].year == vehicleObect.year
        ) {
          alreadySaved = true;
          break;
        }
      }
    } catch (e) {}

    // if already saved, throw alert
    if (alreadySaved) {
      window.alert("Item already saved in list");
      return;
    }

    // proceed with saving vehicle
    $.post(
      MMY.rootUrl + "/Shopify/SaveModel",
      { customerid: cookie.customer, model: vehicleJSONString },
      function (d) {
        if (d.status == "error") alert(d.err);
        else if (d.status == "success" && d.err == "Anon") {
          MMY.RenderVehiclesList(d.vehicles);
        } else if (d.status == "success" && d.err == "Customer") {
          MMY.RenderVehiclesList(FormatVehicleForJSON(d.vehicles));

          // update current vehcles object
          vehicles = FormatVehicleForJSON(d.vehicles);
        }
      }
    );
  }

  // The function changes the entire list styling that the application provides
  function restylingAppElements() {
    const updateTargetNode = document.querySelector("#gv-save-vehicles");
    const vehicleList = updateTargetNode.querySelector("ul");

    if (vehicleList) {
      const updateTargetNodeButtonCheck = vehicleList.querySelector(
        ".add-current-link-wrapper"
      );
      // create the link and added necessary classes
      if (!updateTargetNodeButtonCheck) {
        const newButton = document.createElement("button");
        newButton.textContent = "+ Add Engine";
        newButton.classList.add("btn", "btn--primary", "add-engine");
        newButton.addEventListener("click", (e) => {
          openAppModal(e);
        });
        const addCurrentLinkWrapper = document.createElement("li");
        addCurrentLinkWrapper.className = "add-current-link-wrapper";
        addCurrentLinkWrapper.appendChild(newButton);
        vehicleList.appendChild(addCurrentLinkWrapper);
      }

      // changing <li> elems for layout conversion
      const vehicleListItems = vehicleList.querySelectorAll(
        "#gv-save-vehicles ul li"
      );

      // if at least one is added to the list - hide unnecessary elements on the page
      const hiddenClassOnMyEngineElements = (listNotEmpty) => {
        const myEngineBoxWrapper = document.getElementById(
          "my-engine-box-panel"
        );
        if (myEngineBoxWrapper) {
          if (listNotEmpty) {
            myEngineBoxWrapper.classList.add("hide-unnecessary");
          } else {
            myEngineBoxWrapper.classList.remove("hide-unnecessary");
          }
        }
      };

      let listNotEmpty = false;
      vehicleListItems.forEach((listItem) => {
        if (!listItem.classList.contains("add-current-link-wrapper")) {
          listNotEmpty = true;
          return;
        }
      });
      if (vehicleList.childElementCount <= 1) {
        updateTargetNode.querySelector(".popup").classList.add('hidden');
        document.querySelector('#goVediaListTitle').classList.add('hidden');
        vehicleList.querySelector(".add-current-link-wrapper").classList.add('hidden');
      } else {
        updateTargetNode.querySelector(".popup").classList.remove('hidden');
        document.querySelector('#goVediaListTitle').classList.remove('hidden');
        vehicleList.querySelector(".add-current-link-wrapper").classList.remove('hidden');
      }
      if (listNotEmpty) {
        hiddenClassOnMyEngineElements(true);
      } else {
        hiddenClassOnMyEngineElements(false);
      }

      vehicleListItems.forEach((listItem) => {
        // avoid duplication of parameters
        const existingParametersDiv = listItem.querySelector(
          "div.vehicle-parameters"
        );

        if (!existingParametersDiv) {
          // parameter wrapper
          const parametersDiv = document.createElement("div");
          parametersDiv.className = "vehicle-parameters";
          listItem.appendChild(parametersDiv);

          // retrieving json tag with parameters
          const vehicleLink = listItem.querySelector("a.vehicle-link");
          if (vehicleLink) {
            const jsonContent = vehicleLink.getAttribute("json");
            // parameter string to object
            const jsonObject = JSON.parse(jsonContent);
            const jsonKeys = Object.keys(jsonObject);

            // pass through the object and add each one in the form of <p><b>[key]</b><span>[value]</span></p>
            for (const key of jsonKeys) {
              // <p>
              const pElement = document.createElement("p");

              // <b>
              const bElement = document.createElement("b");
              bElement.textContent = `${key}: `;
              pElement.appendChild(bElement);

              // <span>
              const spanElement = document.createElement("span");
              spanElement.textContent = jsonObject[key];
              pElement.appendChild(spanElement);

              parametersDiv.appendChild(pElement);
            }

            // bottom button organization in <li>
            const delVehicleLink = listItem.querySelector("a.gv-del-vehicle"); // delete button
            delVehicleLink.textContent = "";

            // move the link down and make a button out of it, then for the header duplicate the link and paste it in the beginning
            const titleVehicleLink = listItem.querySelector("a.vehicle-link");
            const titleVehicleLinkClone = titleVehicleLink.cloneNode(true);
            titleVehicleLink.textContent = "Shop Parts";
            titleVehicleLink.classList.add("btn", "btn--primary");
            titleVehicleLink.classList.remove("vehicle-link");

            // buttons wrapper
            const divButtonsWrapper = document.createElement("div");
            divButtonsWrapper.classList.add(
              "buttons-wrapper",
              "flex",
              "items-center",
              "w-full"
            );

            listItem.prepend(titleVehicleLinkClone);
            divButtonsWrapper.appendChild(titleVehicleLink);
            divButtonsWrapper.appendChild(delVehicleLink);
            listItem.appendChild(divButtonsWrapper);
          }
        }
      });
    }
  }
  function rootVehicleListObserver() {
    const rootVehicleList = document.querySelector("#gv-save-vehicles ul");
    const observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === "childList") {
          restylingAppElements();
        }
      }
    });

    const config = {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    };

    if (rootVehicleList) {
      observer.observe(rootVehicleList, config);
    }
  }

  const targetNode = document.querySelector("#gv-save-vehicles");

  const observer = new MutationObserver((mutationsList) => {
    for (let mutation of mutationsList) {
      if (mutation.type === "childList") {
        restylingAppElements();
        rootVehicleListObserver();
      }
    }
  });

  if (targetNode) {
    const config = { childList: true };
    observer.observe(targetNode, config);
  }
  // END auto-part-search APP

  // auto-part-search APP changing modal window
  // -----------------------------------------------------------
  // Next, a button is added to the modal window
  // that will redirect the click to the application
  // button, allowing the selected item to be added to the list.
  function appModalWindowChanging() {
    const modalAddButtonWrapper = document.querySelector(
      ".auto-part-search-bar .mmy-tab.mmy-ops"
    );
    // add new button "add engine"
    const newButton = document.createElement("input");
    newButton.value = "+ Add Engine";
    newButton.type = "submit";
    newButton.classList.add("gv-btn-search", "active");
    modalAddButtonWrapper.appendChild(newButton);

    // new button click handler
    newButton.addEventListener("click", (e) => {
      clickRedirectionOnAddCurrentEngine(e);
      appPopUpElement.classList.remove("show");
      bodyElement.classList.remove("fixed");
    });
  }

  const targetNodeModalApp = document.querySelector(
    ".auto-part-search-bar .gv-mmy-bar .container #gv-mmy"
  );

  const observerModalApp = new MutationObserver((mutationsList) => {
    let trigger = true;
    for (let mutation of mutationsList) {
      if (mutation.type === "childList" && trigger) {
        trigger = false;
        appModalWindowChanging();
      }
    }
  });

  if (targetNodeModalApp) {
    const config = { childList: true };
    // when apps adding window content
    observerModalApp.observe(targetNodeModalApp, config);
  }
  // END auto-part-search APP changing modal window

    class ReadMore {
    constructor(opts) {
      this.opts = {
        ...{
          targetSelector: "",
          containerClassName: "read-more-container",
          actionsContainerClassName: "read-more-actions",
          heightThreshold: 200,
        },
        ...opts,
      };

      this.containerClass = "." + this.opts.containerClassName;
      this.actionsContainerClass = "." + this.opts.actionsContainerClassName;

      this.init();
    }

    init() {
      var blocks = document.querySelectorAll(this.opts.targetSelector);

      for (var i = 0; i < blocks.length; i++) {
        var block = blocks[i];
        var readMoreContainer = block.querySelector(this.containerClass);

        if (
          !readMoreContainer ||
          readMoreContainer.offsetHeight < this.opts.heightThreshold
        ) {
          return;
        }

        if (!block.querySelector(this.actionsContainerClass)) {
          var readMoreActions = document.createElement("div");
          readMoreActions.className = this.opts.actionsContainerClassName;

          var openBtn = document.createElement("button");
          openBtn.setAttribute("type", "button");
          openBtn.setAttribute("data-toggle", "open");
          openBtn.innerText = "Read more";

          var closeBtn = document.createElement("button");
          closeBtn.setAttribute("type", "button");
          closeBtn.setAttribute("data-toggle", "close");
          closeBtn.innerText = "Read less";

          readMoreActions.appendChild(openBtn);
          readMoreActions.appendChild(closeBtn);

          readMoreContainer.parentNode.appendChild(readMoreActions);
        }

        var open = block.querySelector(
          this.actionsContainerClass + ' [data-toggle="open"]'
        );
        var close = block.querySelector(
          this.actionsContainerClass + ' [data-toggle="close"]'
        );
        var toggle = function () {
          block.classList.toggle("read-more-opened");
        };

        if (open) {
          open.addEventListener("click", toggle);
        }

        if (close) {
          close.addEventListener("click", toggle);
        }

        block.classList.add("js-read-more");
      }
    }
  }

  // PartsVu SEO Block
  new ReadMore({ targetSelector: ".partsvu-seo-block" });

  // PDP description tab
  new ReadMore({
    targetSelector: "#tab-description",
    containerClassName: "disclosure__content",
    heightThreshold: 225,
  });

  // @source https://github.com/Shopify/dawn/issues/2166
  window.addEventListener('pageshow', function (e) {
    // @source https://stackoverflow.com/a/42063482
    if (e.persisted || performance.getEntriesByType('navigation')[0].type === 'back_forward') {
      var refreshCart = function () {
        /*document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
          bubbles: true
        }));*/
        var cartIcon = document.getElementById('cart-icon');
        cartIcon.classList.add('is-loading');
        setTimeout(function () {
          var cartItems = document.getElementById('cart-items');
          cartItems.refreshCart().then(() => {
            this.scrollTop = 0;
            cartIcon.classList.remove('is-loading');
          });
        }, 1000);
      };
      
      if (typeof window.theme.scripts.cartItems != 'undefined' && ! customElements.get('cart-items')) {
        var script = document.createElement('script');
        script.src = window.theme.scripts.cartItems;
        script.onload = function () {
          document.getElementById('cart-items').init();
          refreshCart();
        };
        document.body.appendChild(script);
      } else {
        refreshCart();
      }
    }
  });


  // select engine + hidden
  const autoPartSearchBar = document.querySelector('.shopify-app-block.auto-part-search-bar');

  const gvSelectObserver = new MutationObserver(function(mutationsList) {
    for(let mutation of mutationsList) {
      const addedNodes = Array.from(mutation.addedNodes);
      const quantityBreaksWrapper = addedNodes.find(node => node.classList && node.classList.contains('mmy-make'));

      if (quantityBreaksWrapper && quantityBreaksWrapper.childElementCount !== 0) {
        const gvSelectWrapper = document.querySelector('#gv-mmy .mmy-make');
        const gvSelect = gvSelectWrapper.querySelector('select');
        const yamahaOption = gvSelect.querySelector('option[value="Yamaha"]');

        if (yamahaOption) {
          yamahaOption.selected = true;
          const event = new Event('change');
          gvSelect.dispatchEvent(event);

          gvSelectWrapper.style.display = 'none';
        }
      }
    }
  });

  gvSelectObserver.observe(autoPartSearchBar, { childList: true, subtree: true });
});